import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { STORAGE_KEYS } from '../utils/core/localStorageKeys';
import moment from 'moment';
import 'moment/locale/cs';
import { useQuery } from 'react-query';
import * as langApi from '../api/app/languageApi';
import { paths } from '../utils/core/routes';

interface TranslationContextType {
  t: (query: string) => string;
  changeLang: (lang: string) => void;
  lang: string;
  supportedLanguages: string[] | undefined;
}
export const defaultLang = 'EN';
const TranslationContext = createContext<TranslationContextType>({
  t: () => {
    return 'no translations';
  },
  changeLang: () => {},
  lang: defaultLang,
  supportedLanguages: undefined,
});

function TranslationProvider(props: any) {
  const [lang, setLang] = useState<string>();
  const [storageLang, setStorageLang] = useLocalStorage(STORAGE_KEYS.LANGUAGE, lang);
  const [dictionary, setDictionary] = useState<any>(null);

  const { data: defaultLang } = useQuery<string>(['defaultLang'], () =>
    langApi.getDefaultLanguage(),
  );

  const { data: supportedLanguages } = useQuery<string[]>(['supportedLanguages'], () =>
    langApi.getSupportedLanguages(),
  );

  /**
   * Set app language if storage is set
   */
  useEffect(() => {
    if (lang) setStorageLang(lang);
    if (storageLang && !lang) setLang(storageLang);
    if (!lang && !storageLang) setLang(defaultLang);
  }, [storageLang, lang, defaultLang]);

  /**
   * When lang of app changes, import dictionary
   */
  useEffect(() => {
    if (!lang || !supportedLanguages) return;
    if (
      !supportedLanguages?.some(
        (supportedLang) => supportedLang.toLowerCase() === lang.toLowerCase(),
      )
    )
      return setLang(defaultLang);
    const _dictionary = require(`../localization/${lang.toLocaleLowerCase()}.json`);
    if (_dictionary) setDictionary(_dictionary);
    setStorageLang(lang);
    moment.locale(lang.toLocaleLowerCase() === 'en' ? 'en-gb' : lang);
  }, [lang, supportedLanguages]);

  /**
   * Select dictionary
   * @param query
   * @param entity - select dictionary by entity type
   */
  const t = (query: string) => {
    if (!dictionary) return query;
    return getStringFromQuery(query, dictionary);
  };

  /**
   * Get string from query and selected dictionary
   * @param query
   * @param current
   */
  const getStringFromQuery = (query: string, current: any) => {
    const queryArr = query && query.split('.');
    if (!queryArr) return `!${query}`;
    let result = current;
    for (const q of queryArr) {
      if (result && q in result) {
        result = result[q];
      } else {
        result = `!${query}`;
        break;
      }
    }

    return result;
  };

  /**
   * Change language
   * @param lang
   */
  const changeLang = (lang: string) => {
    if (lang === 'DE') {
      moment.locale('de');
    } else if (lang === 'CZ') {
      moment.locale('cs');
    } else moment.locale('en-gb');
    setLang(lang);

    // needed for format change of input fields
    if (!window.location.pathname.includes('fullscreen')) window.location.reload();
  };

  return (
    <TranslationContext.Provider value={{ t, changeLang, lang, supportedLanguages }} {...props} />
  );
}

const useTranslation = () => useContext<TranslationContextType>(TranslationContext);

export { TranslationProvider, useTranslation };
